import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import closeButton from "../../../assets/images/close.png";
import { approveAgreementAction, getAgreementApprovalList } from "../../../services/AppService";
import ApproveActionModal from "../../../components/dashboard/Approvals/ApprovalsPlanEstimate/ApproveActionModal";
import ViewApproveAgreementDetailsModal from "../../../components/dashboard/Approvals/AgreementProcess/ViewApproveAgreementDetailsModal";
import { useTranslation } from "react-i18next";

const ApproveAgreement = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [agreements, setAgreements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedItem, setSelectedItem] = useState(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [approveModalOpen, setApproveModalOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);



    const isOfficer = user?.designation?.toLowerCase().includes("officer");

    useEffect(() => { fetchList(); }, [search, page, size]);

    const fetchList = async () => {
        try {
            setLoading(true);
            const res = await getAgreementApprovalList(search, page, size);
            setAgreements(res.content || []);
            setTotalPages(res.totalPages || 0);
            setTotalElements(res.totalElements || 0);
        } catch (err) { console.error(err); } finally { setLoading(false); }
    };

    const initiateApproval = (item) => {
        setSelectedItem(item);
        setApproveModalOpen(true);
    };

    const handleConfirm = async (id, remark) => {
        try {
            await approveAgreementAction(id, remark);
            setApproveModalOpen(false);
            setViewModalOpen(false);
            fetchList();
        } catch (err) { alert(err.response?.data?.message || "Failed"); }
    };

    return (
        <div className="bg-white shadow-lg rounded-2xl p-6">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
                <h4 className="text-xl font-semibold text-blue">{t('approvals.headingApproveAgreementProcess')}</h4>
                <img src={closeButton} alt="Close" className="w-6 h-6 cursor-pointer" onClick={() => navigate(-1)} />
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                <div className="flex items-center gap-2 text-sm">
                    <label>{t('common.show')}</label>
                    <select
                        value={size}
                        onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
                        className="border rounded px-2"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </select>
                    <label>{t('common.entries')}</label>
                </div>
                <input
                    type="text"
                    placeholder={t('common.search')}
                    className="border rounded px-3 py-1 text-sm"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                />
            </div>

            <div className="overflow-x-auto">

                <table className="w-full border text-sm ctable rounded-lg">
                    <thead className="bg-[#a0adf1] text-white">
                        <tr>
                            <th className="p-2 border">{t('tables.srNo')}</th>
                            <th className="p-2 border">{t('forms.planEstimate.workName')}</th>
                            <th className="p-2 border">{t('tables.tenderProcess.tenderId')}</th>
                            <th className="p-2 border">{t('tables.agreementProcess.agreementNo')}</th>
                            <th className="p-2 border">BPG</th>
                            <th className="p-2 border">{t('tables.view')}</th>
                            <th className="p-2 border">{t('approvals.approvalStatus')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {agreements.map((item, idx) => (
                            <tr key={item.id} className="border-b">
                                <td className="p-2 text-center">{(page * size) + idx + 1}</td>
                                <td className="p-2">{item.workTitle}</td>
                                <td className="p-2">{item.tenderId}</td>
                                <td className="p-2">{item.agreementNo}</td>
                                <td className="p-2">{item.bpg}</td>
                                <td className="p-2 text-center">
                                    <button className="btnImgView p-1" onClick={() => { setSelectedItem(item); setViewModalOpen(true); }}></button>
                                </td>
                                <td className="p-2 text-center">
                                    {item.isApproved ? (
                                        <span className="text-green-600 font-bold text-xs uppercase">Approved</span>
                                    ) : (
                                        <span className="text-yellow-600 font-bold text-xs uppercase">Pending</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4 border-t pt-4">
                <div className="text-sm text-gray-600">
                    {t('common.showing')} {agreements.length > 0 ? page * size + 1 : 0} {t('common.to')} {Math.min((page + 1) * size, totalElements)} {t('common.of')} {totalElements} {t('common.entries')}
                </div>

                <div className="flex items-center gap-2">
                    <button
                        className={`px-3 py-1 border rounded text-sm ${page === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-blue-600 hover:bg-gray-50'}`}
                        onClick={() => setPage(prev => Math.max(0, prev - 1))}
                        disabled={page === 0}
                    >
                        {t('common.previous')}
                    </button>

                    {[...Array(totalPages)].map((_, i) => (
                        <button
                            key={i}
                            className={`px-3 py-1 border rounded text-sm ${page === i ? 'bg-[#a0adf1] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                            onClick={() => setPage(i)}
                        >
                            {i + 1}
                        </button>
                    )).slice(Math.max(0, page - 2), Math.min(totalPages, page + 3))}

                    <button
                        className={`px-3 py-1 border rounded text-sm ${page >= totalPages - 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-blue-600 hover:bg-gray-50'}`}
                        onClick={() => setPage(prev => Math.min(totalPages - 1, prev + 1))}
                        disabled={page >= totalPages - 1}
                    >
                        {t('common.next')}
                    </button>
                </div>
            </div>

            {/* Modals */}

            <ViewApproveAgreementDetailsModal
                isOpen={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                data={selectedItem}
                onApprove={initiateApproval}
            />

            <ApproveActionModal
                isOpen={approveModalOpen}
                onClose={() => setApproveModalOpen(false)}
                onConfirm={handleConfirm}
                estimate={selectedItem}
                title={t('approvals.doApproveAgreementProcess')}
            />
        </div>
    );
};

export default ApproveAgreement;